import { CheckCircle2, ClipboardCopy, Download, MapPin, Navigation, Radar, ShieldCheck, Siren, Wrench, Zap } from 'lucide-react'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { api, type IncidentView, type IncidentsResponse, type LngLat } from '../api'
import { useApp } from '../context'
import { fmtAge } from '../format'
import { PanelShell } from './PanelShell'
import { Badge, Button, Callout, Empty, Loading, Section, Stat, cx } from './ui'

// ------------------------------------------------------------------ wording

export const KIND_LABEL: Record<IncidentView['kind'], string> = { fibre: 'Fibre break', power: 'Power outage', drop: 'Single subscriber' }

const directions = (p: LngLat) => `https://www.google.com/maps/dir/?api=1&destination=${p[1]},${p[0]}`
const hhmm = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
const area = (inc: IncidentView) => {
  const names = inc.byNap.slice(0, 3).map((n) => n.name.replace(/^DEMO-(?:[A-Z]{2}-)?/, ''))
  return names.join(', ') + (inc.byNap.length > 3 ? ` and ${inc.byNap.length - 3} more NAPs` : '')
}

function evidence(inc: IncidentView): string {
  const s = inc.suspects[0]
  if (!s) return ''
  const pct = Math.round(s.confidence * 100)
  const lost = inc.kind === 'power' ? 'lost power' : 'lost light'
  return s.down === s.behind
    ? `All ${s.behind} subscribers whose service runs through it ${lost} at about the same time.`
    : `${s.down} of the ${s.behind} subscribers whose service runs through it ${lost} (${pct}%).`
}

// ------------------------------------------------------------------ banner

/** Always-visible red bar while anything is broken; the first thing anyone sees during trouble. */
export function IncidentBanner({ data, onOpen }: { data: IncidentsResponse | undefined; onOpen: (id?: string) => void }) {
  const serious = data?.incidents.filter((i) => i.kind !== 'drop') ?? []
  if (!serious.length) return null
  const worst = serious[0]!
  const down = serious.reduce((s, i) => s + i.serviceIds.length, 0)
  const unacked = serious.filter((i) => !i.ack).length
  return (
    <div role="alert" className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-danger px-3 py-2 text-sm text-accent-fg">
      <Siren className="h-5 w-5 shrink-0" aria-hidden />
      <strong className="whitespace-nowrap">
        {serious.length} outage{serious.length === 1 ? '' : 's'} · {down.toLocaleString()} subscribers down
      </strong>
      <span className="min-w-0 flex-1 truncate">
        {worst.title} · since {hhmm(worst.startedAt)} ({fmtAge(worst.startedAt)})
        {unacked ? ` · ${unacked} not acknowledged` : ''}
      </span>
      <button onClick={() => onOpen(serious.length === 1 ? worst.id : undefined)} className="rounded-md bg-accent-fg px-2.5 py-1 font-semibold text-danger hover:opacity-90">
        {serious.length === 1 ? 'Open incident' : 'Open incidents'}
      </button>
    </div>
  )
}

// ------------------------------------------------------------------ list

export function IncidentsPanel() {
  const { incidents: data, open, setHighlight, reloadIncidents, canAck } = useApp()
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    if (!data) return
    const serious = data.incidents.filter((i) => i.kind !== 'drop')
    setHighlight({
      cableIds: serious.flatMap((i) => i.suspects.filter((s) => s.kind === 'cable').map((s) => s.id)),
      nodeIds: serious.flatMap((i) => i.byNap.map((n) => n.nodeId)),
      tone: 'danger',
    })
  }, [data, setHighlight])
  if (!data) return <Loading />
  const serious = data.incidents.filter((i) => i.kind !== 'drop')
  const drops = data.incidents.filter((i) => i.kind === 'drop')
  const clear = async () => {
    setBusy(true)
    await api.demoAlarm({ kind: 'clear' }).catch(() => undefined)
    reloadIncidents()
    setBusy(false)
  }
  return (
    <PanelShell title="Incidents" subtitle={<Freshness data={data} />}>
      <Section title={serious.length ? `${serious.length} open outage${serious.length === 1 ? '' : 's'}` : 'Open outages'}>
        {!serious.length && (
          <Callout tone="ok" title="No outages detected">
            Every ONU the OLTs report on has light and power. Single-subscriber problems are listed below.
          </Callout>
        )}
        <ul className="space-y-2">
          {serious.map((i) => (
            <li key={i.id}>
              <button onClick={() => open({ kind: 'incident', id: i.id })} className="w-full rounded-lg border-2 border-danger p-3 text-left hover:bg-surface-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={i.kind === 'power' ? 'warn' : 'danger'}>{KIND_LABEL[i.kind]}</Badge>
                  {i.ack ? <Badge tone="ok">Acknowledged by {i.ack.by}</Badge> : <Badge tone="warn">Not acknowledged</Badge>}
                </div>
                <div className="mt-1.5 font-semibold">{i.title}</div>
                <div className="mt-0.5 text-sm text-muted">
                  Since {hhmm(i.startedAt)} ({fmtAge(i.startedAt)}) · {i.byNap.length} NAPs · {area(i)}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </Section>
      <Section title={`Single subscribers without light · ${drops.length}`}>
        {!drops.length ? (
          <p className="text-sm text-muted">None.</p>
        ) : (
          <ul className="divide-y divide-border text-sm">
            {drops.slice(0, 50).map((i) => (
              <li key={i.id}>
                <button className="w-full py-1.5 text-left hover:text-accent" onClick={() => open({ kind: 'trace', serviceId: i.serviceIds[0]! })}>
                  {i.title} · {i.byNap[0]?.name} · {fmtAge(i.startedAt)}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>
      {data.resolved.length > 0 && (
        <Section title="Recently resolved">
          <ul className="divide-y divide-border text-sm">
            {data.resolved.slice(0, 10).map((r) => (
              <li key={`${r.id}-${r.resolvedAt}`} className="flex items-start gap-2 py-1.5">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-ok" aria-hidden />
                <span>
                  {r.title}
                  <span className="block text-xs text-muted">
                    {hhmm(r.startedAt)} – {hhmm(r.resolvedAt)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}
      {data.synthetic && canAck && (
        <Section title="Demo alarms">
          <p className="mb-2 text-sm text-muted">
            Raise a demo alarm from a cable (“Demo: alarm for a cut here”) or a NAP (“Demo: power outage here”) to see how a real outage looks. Nothing is sent to any OLT.
          </p>
          <Button size="sm" busy={busy} onClick={clear}>
            Clear all demo alarms
          </Button>
        </Section>
      )}
    </PanelShell>
  )
}

function Freshness({ data }: { data: IncidentsResponse }) {
  const [, tick] = useState(0)
  useEffect(() => {
    const t = setInterval(() => tick((x) => x + 1), 5000)
    return () => clearInterval(t)
  }, [])
  const age = (Date.now() - Date.parse(data.refreshedAt)) / 1000
  return (
    <span className={cx(age > 60 && 'font-semibold text-warn')}>
      Alarms from {data.source} · updated {fmtAge(data.refreshedAt)}
      {age > 60 ? ' — data may be stale' : ''}
    </span>
  )
}

// ------------------------------------------------------------------ detail

function Step({ n, children }: { n: number; children: ReactNode }) {
  return (
    <li className="flex gap-2.5">
      <span className="num mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-soft text-xs font-bold text-accent">{n}</span>
      <div className="min-w-0 flex-1 text-sm">{children}</div>
    </li>
  )
}

function RoleSection({ icon, title, who, children }: { icon: ReactNode; title: string; who: string; children: ReactNode }) {
  return (
    <section className="border-t-4 border-border px-4 py-3" aria-label={title}>
      <h3 className="flex items-center gap-2 text-base font-semibold">
        {icon}
        {title}
      </h3>
      <p className="mb-2 text-xs text-muted">{who}</p>
      <ol className="space-y-2.5">{children}</ol>
    </section>
  )
}

const linkBtn = 'inline-flex h-8 items-center gap-1.5 rounded-lg border border-border bg-surface-2 px-2.5 text-[13px] font-medium hover:bg-surface'

export function IncidentPanel({ id }: { id: string }) {
  const { incidents: data, open, setHighlight, focusPath, reloadIncidents, canAck } = useApp()
  const inc = data?.incidents.find((i) => i.id === id)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)
  const [ackError, setAckError] = useState<string | null>(null)

  const focusPoints = useMemo(() => {
    if (!inc) return []
    const pts: LngLat[] = []
    for (const s of inc.suspects) {
      if (s.path) pts.push(...s.path)
      for (const e of s.ends) pts.push(e.location)
    }
    for (const n of inc.byNap) if (n.location) pts.push(n.location)
    return pts
  }, [inc])

  useEffect(() => {
    if (!inc) return
    setHighlight({ cableIds: inc.suspects.filter((s) => s.kind === 'cable').map((s) => s.id), nodeIds: [...inc.byNap.map((n) => n.nodeId), ...inc.suspects.filter((s) => s.kind === 'node').map((s) => s.id)], tone: 'danger' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inc?.id, inc?.serviceIds.length, setHighlight])
  useEffect(() => {
    if (focusPoints.length) focusPath(focusPoints)
    // Fit once when the incident opens, not on every poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inc?.id])

  if (!data) return <Loading />
  if (!inc) {
    const done = data.resolved.find((r) => r.id === id)
    return (
      <PanelShell title={done ? 'Incident resolved' : 'Incident not found'}>
        <div className="p-4">
          <Callout tone={done ? 'ok' : 'neutral'} title={done ? `Service returned at ${hhmm(done.resolvedAt)}` : 'It may have cleared already.'}>
            {done?.title}
          </Callout>
          <Button className="mt-3" onClick={() => open({ kind: 'incidents' })}>
            All incidents
          </Button>
        </div>
      </PanelShell>
    )
  }

  const primary = inc.suspects[0]
  const others = inc.suspects.slice(1)
  const upstream = primary?.ends[0]
  const downstream = primary?.ends[1]
  const oltSuspect = inc.suspects.find((s) => s.kind === 'pon')
  const whole = inc.byPon.every((p) => p.count >= p.behind * 0.8)

  const ack = async () => {
    setBusy(true)
    setAckError(null)
    try {
      await api.ackIncident(inc.id)
      reloadIncidents()
    } catch (e) {
      setAckError(e instanceof Error ? e.message : 'Could not acknowledge')
    } finally {
      setBusy(false)
    }
  }

  // Customers know towns and barangays, not NAP codes.
  const where = inc.localities.length ? `in parts of ${inc.localities.join(' and ')}` : 'in some areas'
  const notice =
    inc.kind === 'power'
      ? `Service advisory: Since ${hhmm(inc.startedAt)} today, some subscribers ${where} are offline because of a power interruption. Service will return when power is restored. If your power is on and you are still offline, please contact us.`
      : `Service advisory: Since ${hhmm(inc.startedAt)} today, some subscribers ${where} have no internet service because of damage to our fibre line. Our field team is working on it. We will post an update as soon as we have one.`

  const copyNotice = async () => {
    try {
      await navigator.clipboard.writeText(notice)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* clipboard blocked: the text is on screen */
    }
  }

  const exportCsv = () => {
    const q = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`
    const rows = [
      ['incident', 'kind', 'started_at', 'account_ref', 'subscriber', 'nap', 'nap_port'],
      ...inc.subscribers.map((x) => [inc.id, inc.kind, inc.startedAt, x.accountRef, x.label, x.nap, x.port]),
    ]
    const url = URL.createObjectURL(new Blob([rows.map((r) => r.map(q).join(',')).join('\n')], { type: 'text/csv' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `outage-${hhmm(inc.startedAt).replace(':', '')}-${inc.subscribers.length}-subscribers.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <PanelShell
      title={KIND_LABEL[inc.kind]}
      subtitle={`Started ${hhmm(inc.startedAt)} (${fmtAge(inc.startedAt)})`}
      badge={inc.ack ? <Badge tone="ok">Acknowledged by {inc.ack.by} at {hhmm(inc.ack.at)}</Badge> : <Badge tone="danger">Not acknowledged</Badge>}
    >
      <Section title="Impact">
        <div className="grid grid-cols-3 gap-2">
          <Stat label="Subscribers down" value={inc.serviceIds.length.toLocaleString()} tone="danger" />
          <Stat label="NAPs" value={inc.byNap.length} />
          <Stat label="PON ports" value={inc.byPon.length} />
        </div>
      </Section>

      {primary && (
        <Section title="Most likely location">
          <div className="rounded-lg border-2 border-danger bg-danger-soft p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-danger">{primary.what}</div>
            <div className="text-lg font-bold leading-tight">{primary.name}</div>
            <div className="text-sm text-muted">{primary.detail}</div>
            <p className="mt-2 text-sm">{evidence(inc)}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button size="sm" onClick={() => focusPath(focusPoints)}>
                <MapPin className="h-4 w-4" aria-hidden /> Show on map
              </Button>
              {primary.ends.map((e) => (
                <a key={e.nodeId} className={linkBtn} href={directions(e.location)} target="_blank" rel="noreferrer">
                  <Navigation className="h-4 w-4" aria-hidden /> Directions to {e.name.replace(/^DEMO-(?:[A-Z]{2}-)?/, '')}
                </a>
              ))}
            </div>
          </div>
          {others.length > 0 && (
            <div className="mt-2 text-sm">
              <p className="text-muted">Alarms alone cannot tell these apart from the above; the OTDR shot will:</p>
              <ul className="mt-1 list-disc pl-5">
                {others.map((s) => (
                  <li key={`${s.kind}:${s.id}`}>
                    {s.what} <strong>{s.name}</strong> <span className="text-muted">({s.detail})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Section>
      )}

      {inc.kind === 'fibre' && (
        <RoleSection icon={<Wrench className="h-5 w-5 text-accent" aria-hidden />} title="OSP field team" who="Find and repair the break">
          {upstream && (
            <Step n={1}>
              Go to <strong>{upstream.name}</strong>, the start of the suspect cable.{' '}
              <a className="font-medium text-accent underline" href={directions(upstream.location)} target="_blank" rel="noreferrer">
                Directions
              </a>
            </Step>
          )}
          {inc.otdrFrom && inc.otdrFrom.kind === 'core' && (
            <Step n={2}>
              Shoot the OTDR on <strong>{primary?.name} core {inc.otdrFrom.core}</strong> toward {downstream?.name ?? 'the next node'}, then enter the event distance.
              <div className="mt-1.5">
                <Button size="sm" variant="primary" onClick={() => open({ kind: 'otdr', from: inc.otdrFrom! })}>
                  <Radar className="h-4 w-4" aria-hidden /> Enter OTDR distance
                </Button>
              </div>
            </Step>
          )}
          <Step n={3}>If the OTDR shows the event at a closure, open that closure first: check trays and splices before walking the cable.</Step>
          <Step n={4}>After the repair, watch this page. The incident closes by itself when the ONUs report light again.</Step>
        </RoleSection>
      )}
      {inc.kind === 'power' && (
        <RoleSection icon={<Wrench className="h-5 w-5 text-accent" aria-hidden />} title="OSP field team" who="Usually no fibre work">
          <Step n={1}>The ONUs reported “dying gasp”: they lost electrical power, not light. The fibre is probably fine.</Step>
          <Step n={2}>Check for a utility power interruption around {area(inc)}. Only dispatch if power is on and ONUs stay offline.</Step>
        </RoleSection>
      )}

      <RoleSection icon={<ShieldCheck className="h-5 w-5 text-accent" aria-hidden />} title="NOC" who="Own the incident and keep customers informed">
        <Step n={1}>
          {inc.ack ? (
            <span>
              Acknowledged by <strong>{inc.ack.by}</strong> at {hhmm(inc.ack.at)}.
            </span>
          ) : canAck ? (
            <>
              Acknowledge so everyone knows it is being handled.
              <div className="mt-1.5">
                <Button size="sm" variant="primary" busy={busy} onClick={ack}>
                  Acknowledge
                </Button>
              </div>
              {ackError && <p className="mt-1 text-xs text-danger">{ackError}</p>}
            </>
          ) : (
            'Ask the NOC or an OSP engineer to acknowledge it.'
          )}
        </Step>
        <Step n={2}>
          Export the affected list for the service desk.
          <div className="mt-1.5">
            <Button size="sm" onClick={exportCsv}>
              <Download className="h-4 w-4" aria-hidden /> Export affected list (CSV)
            </Button>
          </div>
        </Step>
        <Step n={3}>
          Post a customer advisory. No repair time is promised.
          <blockquote className="mt-1.5 rounded-lg border border-border bg-surface-2 p-2 text-sm">{notice}</blockquote>
          <Button size="sm" className="mt-1.5" onClick={copyNotice}>
            <ClipboardCopy className="h-4 w-4" aria-hidden /> {copied ? 'Copied' : 'Copy advisory'}
          </Button>
        </Step>
      </RoleSection>

      <RoleSection icon={<Zap className="h-5 w-5 text-accent" aria-hidden />} title="IT / OLT" who="Rule the OLT in or out">
        {oltSuspect ? (
          <Step n={1}>
            Check <strong>{oltSuspect.name}</strong> on the OLT: port state, optic and alarms. If the port is up, the fault is in the plant and the OSP team has it.
          </Step>
        ) : inc.kind === 'fibre' ? (
          <Step n={1}>
            The pattern points to the plant: one {primary?.what ?? 'element'} carries every affected subscriber across {inc.byPon.length} PON port{inc.byPon.length === 1 ? '' : 's'}. No OLT action is indicated
            {whole ? '' : ', and other subscribers on the same ports are still online'}.
          </Step>
        ) : (
          <Step n={1}>No OLT action indicated for a power outage. Check the POP's own power alarms only if the POP is inside the affected area.</Step>
        )}
      </RoleSection>

      <Section title="Where subscribers are down">
        <ul className="divide-y divide-border text-sm">
          {inc.byNap.slice(0, 40).map((n) => (
            <li key={n.nodeId}>
              <button className="flex w-full justify-between py-1.5 text-left hover:text-accent" onClick={() => open({ kind: 'node', id: n.nodeId })}>
                <span>{n.name}</span>
                <span className="num font-medium">{n.count}</span>
              </button>
            </li>
          ))}
        </ul>
        {inc.byNap.length > 40 && <p className="mt-1 text-xs text-muted">and {inc.byNap.length - 40} more NAPs (all in the CSV export)</p>}
      </Section>

      <Section title="Timeline">
        <ol className="space-y-1 text-sm">
          <li>
            <span className="num text-muted">{hhmm(inc.startedAt)}</span> First ONU alarm
          </li>
          <li>
            <span className="num text-muted">{hhmm(inc.detectedAt)}</span> Detected and correlated by FNMS
          </li>
          {inc.ack && (
            <li>
              <span className="num text-muted">{hhmm(inc.ack.at)}</span> Acknowledged by {inc.ack.by}
            </li>
          )}
        </ol>
      </Section>
      {!inc.suspects.length && inc.kind === 'drop' && <Empty title="One subscriber only">Check the drop cable and ONU.</Empty>}
    </PanelShell>
  )
}
